/**
 * DECISIONS 209 (5) (the owner: "Let's do respawn at base with ship as option"): with wave ships every arrival comes back at
 * its side's base, by the pad, and the parked ship is a ride to the side's forward point (`shipPads[team].dest`). A bot takes
 * it when it is quicker than its feet: its walk to the ramp (or the ship's departure, whichever is later) plus the run and the
 * walk on from the landing, beating its walk from the base to where its squad is sent (core/drops.ts arrivalAim) by
 * SHIP_RIDE.marginSeconds. Its squad decides together — from its first living member's place, not each its own — and only at
 * the base (within SHIP_RIDE.basePeople of the pad). A free vehicle is the APC loop's to give first (ai/actionBot.ts reads
 * this after it). Pure; on the side's knowledge of its own squads and its own ship.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import { DROPSHIP, PHYS_DT } from '../constants';
import { vecDot, vecNormalize } from '../math';
import { dacos } from './dmath';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { arrivalAim } from './drops';
import { squadOf } from './squads';
import { boardingOpen, boardingSortie, padRampFoot } from './dropship';
import { routeCost } from './planetRoutes';
import { roadsOf } from './roads';

/** The ride's dials: how near the pad a unit must stand to weigh it (people), and by how much the ride must win (seconds). */
export const SHIP_RIDE = { basePeople: 40, marginSeconds: 5 };

/** The ramp's foot for `unit` to walk to and board its side's parked ship, or null (walk, or nothing to board). */
export function shipRideFor(state: GameState, unit: PlayerState, tick: number): Vec3 | null {
  const team = unit.teamIndex, pad = state.shipPads?.[team];
  if (!pad?.dest || !unit.alive || unit.boarded || unit.mount || unit.planetIndex !== pad.planetIndex || pad.dest.planetIndex !== pad.planetIndex) return null;
  const R = state.planets[pad.planetIndex].radius, person = PERSON_BODY_HEIGHT * personSetOf(state).k, walk = personSetOf(state).walkSpeed;
  const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * R;
  if (arc(unit.surfaceDir, pad.dir) > SHIP_RIDE.basePeople * person || !boardingOpen(state, team)) return null;
  // (its squad together: from its first living member not yet aboard)
  const sq = squadOf(state, unit.id);
  const lead = sq ? state.players.find(p => sq.unitIds.includes(p.id) && p.alive && !p.boarded && p.planetIndex === pad.planetIndex) ?? unit : unit;
  const goal = arrivalAim(state, lead.id, tick) ?? { planetIndex: pad.dest.planetIndex, surfaceDir: pad.dest.dir };
  if (goal.planetIndex !== pad.planetIndex) return null;
  const ramp = padRampFoot(state, team);
  const q = boardingSortie(state, team), first = q?.units.some(id => state.players.find(p => p.id === id)?.boarded);
  const takeoff = first ? Math.max(0, q!.since + Math.round(DROPSHIP.sortieSeconds / PHYS_DT) - tick) * PHYS_DT : DROPSHIP.sortieSeconds;
  // (§0.11 step C, GameConfig.planetRoutes: the walks on the planet's route graph, not straight)
  const planet = state.planets[pad.planetIndex];
  const roads = roadsOf(state, pad.planetIndex);   // (216 (4): the roads, with them on)
  const walked = (a: Vec3, b: Vec3) => (state.config.planetRoutes ? routeCost(planet, a, b, 'foot', roads) : arc(a, b)) / walk;
  const ride = Math.max(arc(lead.surfaceDir, ramp.surfaceDir) / walk, takeoff) + pad.dest.runSeconds + walked(pad.dest.dir, goal.surfaceDir);
  return ride + SHIP_RIDE.marginSeconds < walked(lead.surfaceDir, goal.surfaceDir) ? ramp.surfaceDir : null;
}
